import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { AuthMode } from '../data-models/auth-mode.model';
import { AuthEmailCodeResponse } from '../response-models/auth-email-code-response.model';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';

/**
 * Six-digit email codes: `/auth/email-codes/*` (hub `auth-controller.ts`). Sends the verification, login and
 * password-reset codes, and redeems a typed code for the Firebase `oobCode` behind it.
 * Failures reject with an `ApiError` (`message` is the hub's error, `code` its typed error code).
 */
@Injectable({
  providedIn: 'root'
})
export class AuthEmailCodeService extends ApiService {
  constructor() {
    super('auth/email-codes');
  }

  /**
   * Resend the verification email for the signed-in user (identified by the bearer token).
   * An `ApiError` with code `on_timeout` (429) means a code was sent too recently.
   */
  async resendVerificationEmail(): Promise<void> {
    return firstValueFrom(this.request('POST', { path: 'verification' }, () => undefined, {}));
  }

  /**
   * Send the password-reset email with its six-digit code. The hub answers success for unknown
   * addresses too, so success doesn't imply the account exists.
   */
  async sendResetPassword(email: string): Promise<void> {
    return firstValueFrom(this.request('POST', { path: 'reset' }, () => undefined, { email }));
  }

  async sendEmailLoginCode(email: string): Promise<void> {
    return firstValueFrom(this.request('POST', { path: 'login' }, () => undefined, { email }));
  }

  /**
   * Exchange a typed six-digit code for the Firebase `oobCode` behind it. `mode` picks which flow's code
   * to redeem ('reset' feeds `confirmPasswordReset`, 'verify' feeds `applyActionCode`).
   * A wrong, expired or spent code rejects with a 400 `ApiError`.
   */
  async verifyAuthEmailCode(email: string, code: string, mode: AuthMode): Promise<AuthEmailCodeResponse> {
    return firstValueFrom(this.request('POST', { path: 'redeem' }, toAuthEmailCode, { email, code, mode }));
  }

  /** Verify a reset code when the user arrives from the link in a reset email (`codeId` is one path segment). */
  async verifyResetCodeWithId(codeId: string, code: string): Promise<AuthEmailCodeResponse> {
    return firstValueFrom(this.request('POST', { id: codeId, path: 'redeem' }, toAuthEmailCode, { code }));
  }
}

function toAuthEmailCode(data: unknown): AuthEmailCodeResponse {
  const source = isRecord(data) ? data : {};
  return { oobCode: nullableString(source['oobCode']) ?? '', email: nullableString(source['email']) ?? null };
}
